/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Spin,
  Col,
  Drawer,
  ImageField,
  notification,
  Row,
  Space,
  TextField,
} from "@pankod/refine-antd";
import { useNavigation } from "@pankod/refine-core";
import { useLocation } from "@pankod/refine-react-router-v6";
import { TOKEN_KEY } from "authProvider";
import axios from "axios";
import { AddProductToCart } from "components/catalog";
import { capitalizeFirstLetter, getCartInfo } from "components/helper";
import { useCanAccessByRole, useFetchCart } from "hooks";
import {
  CartProductLookup,
  fetchCartProduct,
} from "hooks/cartProductAvailability";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCounterpartyId, getOutletId } from "store/slices/appSlice";
import {
  getCartItems,
  setCartItems,
  setProducts,
} from "store/slices/cartSlice";
import { closeCartDrawer, hasOpenCartDrawer } from "store/slices/modalsSlice";
import { AppState } from "store/store";
import "./CartDrawer.css";

export interface ICartDrawerProps {}

export const CartDrawer: FC = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const location = useLocation();
  const dispatch = useDispatch();
  const { push, show } = useNavigation();
  const cartDrawerOpen = useSelector<AppState, boolean>(hasOpenCartDrawer);
  const counterpartyId = useSelector(getCounterpartyId);
  const outletId = useSelector(getOutletId);
  const cartItems = useSelector(getCartItems);
  const [productLookup, setProductLookup] = useState<
    Record<number, CartProductLookup>
  >({});
  const [loadedKey, setLoadedKey] = useState("");
  const cartIdsKey = cartItems.map((item) => item.productId).join(",");
  const contextKey = `${counterpartyId}:${outletId}:${cartIdsKey}`;
  const isLoaded = cartDrawerOpen && loadedKey === contextKey;
  const canAccess = useCanAccessByRole(["buyer"]);
  const { fetchCart, canFetch } = useFetchCart();

  useEffect(() => {
    if (counterpartyId && outletId && canFetch && canAccess) {
      fetchCart();
    }
  }, [outletId, canFetch, canAccess, counterpartyId]);

  // Only cart rows define what should be displayed. The product API adds
  // optional details independently for each row, never filtering it out.
  useEffect(() => {
    if (
      !cartDrawerOpen ||
      !canAccess ||
      counterpartyId == null ||
      outletId == null
    ) {
      return;
    }
    let cancelled = false;
    setLoadedKey("");

    const ids = cartIdsKey ? cartIdsKey.split(",").map((id) => Number(id)) : [];

    Promise.all(
      ids.map((id) => fetchCartProduct(id, counterpartyId, outletId)),
    ).then((results) => {
      if (cancelled) return;
      const byId: Record<number, CartProductLookup> = {};
      results.forEach((result) => {
        byId[result.id] = result;
      });
      setProductLookup(byId);
      // The existing checkout calculations must never price inactive products.
      dispatch(
        setProducts(
          results.flatMap((result) =>
            result.state === "active" ? [result.product] : [],
          ),
        ),
      );
      setLoadedKey(contextKey);
    });

    return () => {
      cancelled = true;
    };
  }, [
    cartDrawerOpen,
    canAccess,
    counterpartyId,
    outletId,
    cartIdsKey,
    contextKey,
    dispatch,
  ]);

  const activeProducts = cartItems.flatMap((item) => {
    const result = productLookup[item.productId];
    return isLoaded && result?.state === "active" ? [result.product] : [];
  });
  const canCheckout =
    cartItems.length > 0 &&
    isLoaded &&
    cartItems.every(
      (item) => productLookup[item.productId]?.state === "active",
    );

  const removeCartItem = async (
    productName: string,
    productId: number,
    quantity: number,
  ) => {
    if (!canAccess) return;
    try {
      const { data, status } = await axios.delete(
        `${process.env.REACT_APP_API_URL}/b2b/cart?c=${counterpartyId}&o=${outletId}`,

        {
          data: [
            {
              productId: productId,
              quantity: quantity,
            },
          ],
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      );
      if (status === 200) {
        notification.open({
          message: `Видалено з кошика`,
          description: `${productName}`,
          type: "success",
        });

        dispatch(setCartItems(data));
        notification.destroy(productId);
      }
    } catch (e) {
      //TODO: show error
    }
  };

  const changeCartItemQuantity = async (
    productName: string,
    productId: number,
    newQuantity: number,
  ) => {
    if (!token || !outletId || !counterpartyId || !canAccess) return;

    try {
      const { data, status } = await axios.put(
        `${process.env.REACT_APP_API_URL}/b2b/cart?c=${counterpartyId}&o=${outletId}`,
        [
          {
            productId: productId,
            quantity: newQuantity,
          },
        ],
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      );
      if (status === 200) {
        notification.open({
          description: (
            <span>
              <b>{productName}</b> - нова кількість <b>{newQuantity}</b>
            </span>
          ),
          message: `Товар в кошику змінено`,

          type: "success",
        });

        dispatch(setCartItems(data));
        notification.destroy(productId);
      }
    } catch (e) {
      //TODO: show error
    }
  };
  const { sum, modBalace, modedProducts } = useMemo(() => {
    return getCartInfo(cartItems, activeProducts);
  }, [cartItems, productLookup, isLoaded]);

  useEffect(() => {
    if (modBalace && modedProducts.length > 0) {
      modedProducts.forEach((product) => {
        notification.error({
          message: "УВАГА! Кількість товару в кошику змінилась",
          type: "error",
          key: product.id,
          duration: 0,

          description: (
            <Row>
              <Col>
                <b>{capitalizeFirstLetter(product.name)}</b> <br></br>з{" "}
                <b>{product.prev}</b> на <b>{product.new}</b>
              </Col>
              <Col
                style={{
                  marginTop: "8px",
                }}
              >
                <Space align="end">
                  <Button
                    danger
                    onClick={() => {
                      removeCartItem(product.name, product.id, product.prev);
                    }}
                  >
                    Видалити
                  </Button>
                  {product.new !== 0 && (
                    <Button
                      onClick={() =>
                        changeCartItemQuantity(
                          product.name,
                          product.id,
                          product.new,
                        )
                      }
                    >
                      Залишити {product.new} шт.
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          ),
        });
      });
    }
  }, [modBalace, modedProducts, location.pathname]);

  return (
    <Drawer
      key={"cartDrawer"}
      title={"Кошик"}
      placement="right"
      footer={
        <Row className="summary">
          <Col
            style={{
              marginRight: "8px",
            }}
          >
            <span>Всього найменувань </span>
            <span>
              <b>{cartItems.length}</b>,
            </span>
            <span>на сумму </span>
            <span>
              <b>{sum} грн.</b>
            </span>
          </Col>

          <Col
            style={{
              marginRight: "8px",
            }}
          >
            <Button
              danger
              disabled={!canCheckout}
              onClick={() => {
                dispatch(closeCartDrawer());
                push("checkout");
              }}
            >
              Оформити замовлення
            </Button>
          </Col>
        </Row>
      }
      size={"large"}
      style={{
        height: "100%",
      }}
      open={cartDrawerOpen}
      onClose={() => dispatch(closeCartDrawer())}
    >
      {cartItems.length > 0 && !isLoaded && <Spin tip="Перевірка товарів..." />}
      {isLoaded &&
        cartItems.map((cartItem) => {
          const lookup = productLookup[cartItem.productId];
          if (!lookup || lookup.state !== "active") {
            const name =
              lookup?.state === "inactive" && lookup.name
                ? capitalizeFirstLetter(lookup.name)
                : `Товар №${cartItem.productId}`;
            const message =
              lookup?.state === "inactive"
                ? "Товар наразі недоступний"
                : "Не вдалося перевірити доступність товару";
            return (
              <Row
                key={cartItem.productId}
                style={{
                  alignItems: "center",
                  padding: "12px 8px",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #e5e5e5",
                  color: "#777",
                }}
              >
                <Col span={16}>
                  <div>
                    <b>{name}</b>
                  </div>
                  <div>{message}</div>
                  <div>Кількість у кошику: {cartItem.quantity}</div>
                </Col>
                <Col span={8} style={{ textAlign: "right" }}>
                  <Button
                    danger
                    onClick={() =>
                      removeCartItem(
                        name,
                        cartItem.productId,
                        cartItem.quantity,
                      )
                    }
                  >
                    Видалити
                  </Button>
                </Col>
              </Row>
            );
          }
          const product = lookup.product;

          let media = product.media.find((e) => e.collectionName === "main");
          if (!media && product.media.length > 0) {
            media = product.media[0];
          }

          const priceField = () => {
            if (product.discountPrice !== 0) {
              return (
                <Row>
                  <Col span={24}>
                    <del className="old-price">
                      {product.prices[0].price} <span>грн.</span>
                    </del>
                  </Col>
                  <Col span={24}>
                    <div className="price-discount">
                      {product.discountPrice} <span>грн.</span>
                    </div>
                  </Col>
                </Row>
              );
            }
            return (
              <TextField
                className="price"
                value={`${product.prices[0].price} грн.`}
              />
            );
          };

          return (
            <Row
              key={`${product.code}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingBottom: "8px",
                paddingTop: "8px",
                borderBottom: "solid 1px #efefef",
              }}
            >
              <Col
                span={4}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImageField
                  preview={false}
                  value={`${process.env.REACT_APP_IMAGE_PATH}${media?.small}`}
                  height={64}
                />
              </Col>
              <Col span={8}>
                {" "}
                {
                  <TextField
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() => show("products", product.id)}
                    value={capitalizeFirstLetter(product.name)}
                  />
                }
              </Col>
              <Col span={4}>{priceField()}</Col>
              <Col span={8}>
                <AddProductToCart
                  key={`${product.id}-${cartItem.quantity}`}
                  productId={product.id}
                  cartQuantity={cartItem.quantity}
                  initialValue={cartItem.quantity}
                  productName={product.name}
                  onChangeBalance={(productId: number) => {
                    notification.destroy(productId);
                  }}
                  liveUpdate
                  canDelete
                />
              </Col>
            </Row>
          );
        })}
    </Drawer>
  );
};
